import { useEffect, useReducer, useCallback, useMemo, useContext, useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  GoogleAuthProvider,
  GithubAuthProvider,
  TwitterAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  User,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { FIREBASE_API, FIREBASE_DEV_API } from "src/config-global";
import {
  ActionMapType,
  AuthStateType,
  AuthUserType,
  FirebaseContextType,
} from "src/types/user";
import { createContext } from "react";
import SessionTimeoutDialog from "src/components/SessionTimeoutDialog";

const firebaseApp =
  process.env.REACT_APP_PROD == "true"
    ? initializeApp(FIREBASE_API)
    : initializeApp(FIREBASE_DEV_API);

const AUTH = getAuth(firebaseApp);

const DB = getFirestore(firebaseApp);

enum Types {
  INITIAL = "INITIAL",
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
};

type Action = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: Action) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  return state;
};

type Props = {
  children: React.ReactNode;
};

export const AuthContext = createContext({} as FirebaseContextType);

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [sessionTimeoutDialog, setSessionTimeoutDialog] = useState({
    open: false,
    timeRemaining: 300, // 5分
  });

  // トークンを更新する関数
  const updateToken = useCallback(async (user: User) => {
    try {
      const accessToken = await user.getIdToken(true); // force refresh
      return accessToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return null;
    }
  }, []);

  const initialize = useCallback(() => {
    let tokenRefreshInterval: NodeJS.Timeout | null = null;
    let activityCheckInterval: NodeJS.Timeout | null = null;
    let lastActivityTime = Date.now();
    let currentUserState: AuthUserType = null;
    
    // ユーザーアクティビティを検知する関数
    const updateActivityTime = () => {
      lastActivityTime = Date.now();
      // セッションタイムアウト機能は無効化
      // if (sessionTimeoutDialog.open) {
      //   setSessionTimeoutDialog({ open: false, timeRemaining: 300 });
      // }
    };

    // アクティビティイベントリスナーを追加
    const addActivityListeners = () => {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.addEventListener(event, updateActivityTime, true);
      });
    };

    // アクティビティイベントリスナーを削除
    const removeActivityListeners = () => {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.removeEventListener(event, updateActivityTime, true);
      });
    };

    try {
      const unsubscribe = onAuthStateChanged(AUTH, async (user) => {
        if (user) {
          const userProfile = doc(DB, "users", user.uid);

          const docSnap = await getDoc(userProfile);

          const profile = docSnap.data();

          // Firebase IDトークンを取得
          const accessToken = await user.getIdToken();

          const userData = {
            ...user,
            ...profile,
            id: user.uid,
            accessToken, // IDトークンをaccessTokenとして追加
          };
          
          currentUserState = userData;
          
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: userData,
            },
          });

          // 既存のインターバルをクリア
          if (tokenRefreshInterval) {
            clearInterval(tokenRefreshInterval);
          }
          if (activityCheckInterval) {
            clearInterval(activityCheckInterval);
          }

          // アクティビティリスナーを追加
          addActivityListeners();
          lastActivityTime = Date.now();

          // トークンの自動更新を設定（45分ごとに更新）
          tokenRefreshInterval = setInterval(async () => {
            try {
              const currentUser = AUTH.currentUser;
              if (currentUser) {
                const newToken = await updateToken(currentUser);
                if (newToken && currentUserState) {
                  const updatedUserData = {
                    ...currentUserState,
                    accessToken: newToken,
                  };
                  
                  currentUserState = updatedUserData;
                  
                  dispatch({
                    type: Types.INITIAL,
                    payload: {
                      user: updatedUserData,
                    },
                  });
                }
              }
            } catch (error) {
              console.error("Automatic token refresh failed:", error);
              // トークン更新に失敗した場合、ユーザー状態をクリアしてログアウト処理
              currentUserState = null;
              await signOut(AUTH);
            }
          }, 45 * 60 * 1000); // 45分

          // アクティビティチェック（5分ごと）
          activityCheckInterval = setInterval(() => {
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivityTime;
            
            // 30分間アクティビティがない場合、トークンを更新
            if (timeSinceLastActivity > 30 * 60 * 1000) {
              const currentUser = AUTH.currentUser;
              if (currentUser) {
                updateToken(currentUser).catch((error) => {
                  console.error("Activity-based token refresh failed:", error);
                });
              }
            }
            
            // セッションタイムアウト機能は無効化
            // if (timeSinceLastActivity > 40 * 60 * 1000 && !sessionTimeoutDialog.open) {
            //   setSessionTimeoutDialog({
            //     open: true,
            //     timeRemaining: 300, // 5分
            //   });
            // }
          }, 5 * 60 * 1000); // 5分
        } else {
          // ユーザーがログアウトした場合、インターバルをクリア
          if (tokenRefreshInterval) {
            clearInterval(tokenRefreshInterval);
            tokenRefreshInterval = null;
          }
          if (activityCheckInterval) {
            clearInterval(activityCheckInterval);
            activityCheckInterval = null;
          }

          // アクティビティリスナーを削除
          removeActivityListeners();

          currentUserState = null;

          dispatch({
            type: Types.INITIAL,
            payload: {
              user: null,
            },
          });
        }
      });

      // クリーンアップ関数を返す
      return () => {
        unsubscribe();
        if (tokenRefreshInterval) {
          clearInterval(tokenRefreshInterval);
        }
        if (activityCheckInterval) {
          clearInterval(activityCheckInterval);
        }
        removeActivityListeners();
      };
    } catch (error) {
      console.error("Auth initialization failed:", error);
      currentUserState = null;
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, [updateToken, sessionTimeoutDialog.open]);

  useEffect(() => {
    const cleanup = initialize();
    return cleanup;
  }, [initialize]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(AUTH, email, password);
      return result;
    } catch (error) {
      // Firebase認証エラーをそのまま投げる
      throw error;
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  const loginWithGithub = useCallback(async () => {
    const provider = new GithubAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  const loginWithTwitter = useCallback(async () => {
    const provider = new TwitterAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, params: any) => {
      const newUser = await createUserWithEmailAndPassword(
        AUTH,
        email,
        password
      );

      //await sendEmailVerification(newUser.user);

      const userProfile = doc(collection(DB, "users"), newUser.user?.uid);

      await setDoc(userProfile, {
        uid: newUser.user?.uid,
        email,
        plan: "free",
        role: params.role,
        dashboard_type: params.dashboardType,
        company_name: params.companyName,
      });
    },
    []
  );

  const sendVerificationEmail = async () => {
    if (AUTH.currentUser) {
      //return await sendEmailVerification(AUTH.currentUser as User);
      return;
    } else {
      console.error("Not signed in yet");
      return;
    }
  };

  // LOGOUT
  const logout = useCallback(async () => {
    await signOut(AUTH);
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email: string) => {
    await sendPasswordResetEmail(AUTH, email);
  }, []);

  // USER REFRESH
  const refreshUser = useCallback(async () => {
    if (state.user) {
      const userRef = doc(DB, "users", state.user.uid);
      const docSnap = await getDoc(userRef);
      const profile = docSnap.data();

      // 現在のユーザーから新しいトークンを取得
      const currentUser = AUTH.currentUser;
      const accessToken = currentUser ? await updateToken(currentUser) : null;

      dispatch({
        type: Types.INITIAL,
        payload: {
          user: {
            ...state.user,
            ...profile,
            accessToken, // 更新されたトークンを設定
          },
        },
      });
    } else {
      console.error("User not authenticated");
    }
  }, [state.user, updateToken]);

  const changeAvailablePublic = useCallback(
    async (type: string) => {
      console.log("changeAvailablePublic", type);
      //userのavailablePublicの数を一つ減らします
      if (state.user) {
        const userRef = doc(DB, "users", state.user.uid);
        const docSnap = await getDoc(userRef);

        const profile = docSnap.data();
        const available_public = profile?.available_public;

        if (type === "increase") {
          await updateDoc(userRef, {
            available_public: available_public + 1,
          });
        } else {
          await updateDoc(userRef, {
            available_public: available_public - 1,
          });
        }
        //更新した結果に変更する
        const docSnap2 = await getDoc(userRef);
        const profile2 = docSnap2.data();
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: {
              ...state.user,
              ...profile2,
            },
          },
        });
      } else {
        console.error("User not authenticated");
      }
    },
    [state.user]
  );

  // セッション延長処理
  const extendSession = useCallback(async () => {
    if (state.user) {
      try {
        const currentUser = AUTH.currentUser;
        if (currentUser) {
          await updateToken(currentUser);
          setSessionTimeoutDialog({ open: false, timeRemaining: 300 });
        }
      } catch (error) {
        console.error("Session extension failed:", error);
        await logout();
      }
    }
  }, [state.user, updateToken]);

  // セッション期限切れ時のログアウト処理
  const handleSessionTimeout = useCallback(async () => {
    setSessionTimeoutDialog({ open: false, timeRemaining: 300 });
    await logout();
  }, []);

  // const checkAuthenticated = state.user?.emailVerified
  //   ? "authenticated"
  //   : "unauthenticated";

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      emailVerified: state.user?.emailVerified,
      method: "firebase",
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
      login,
      logout,
      register,
      forgotPassword,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter,
      sendVerificationEmail,
      refreshUser,
      changeAvailablePublic,
      extendSession,
      handleSessionTimeout,
      sessionTimeoutDialog,
    }),
    [
      status,
      state.user,
      login,
      logout,
      register,
      forgotPassword,
      loginWithGithub,
      loginWithGoogle,
      loginWithTwitter,
      refreshUser,
      changeAvailablePublic,
      extendSession,
      handleSessionTimeout,
      sessionTimeoutDialog,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
      {/* セッションタイムアウト機能は無効化 */}
      {/* {state.user && (
        <SessionTimeoutDialog
          open={sessionTimeoutDialog.open}
          onExtendSession={extendSession}
          onLogout={handleSessionTimeout}
          timeRemaining={sessionTimeoutDialog.timeRemaining}
        />
      )} */}
    </AuthContext.Provider>
  );
}

export const useAuthData = () => useContext(AuthContext) as FirebaseContextType;
